import type { Flag, Level } from './Level'
import type { Erreur, ErreurState } from './Erreur'
import type {
  Regles,
  ReglesMembre,
  RegleModelePreCallback,
  ModelValidateurResult,
  ValidateurResult,
  RegleModel,
  Regle,
  ContextDefault,
} from './Regle.js'
import { objectMergeKeys } from '@/features/shared/tools'
import { mergeModelState } from '@/features/domaine/model-state-tools'

const LEVEL_DEFAULT_VALUE = 0
const MESSAGE_DEFAULT = 'Une erreur est survenue'

// Type de commodite, pour ne pas avoir de any partout
type ErreurResult<TLevel extends number> = {
  [key: string]: Erreur<object, TLevel>
} & ErreurState<TLevel>

export class Validateur<
  TModel extends object,
  TContext = ContextDefault,
  TLevel extends Level = Level,
  TFlag extends Flag = Flag,
  TGlobalModele extends object = TModel,
> {
  #regles: Readonly<Regles<TModel, TContext, TLevel, TFlag, TGlobalModele>>
  constructor(regles: Regles<TModel, TContext, TLevel, TFlag, TGlobalModele>) {
    this.#regles = regles
  }
  static readonly MESSAGE_DEFAULT = MESSAGE_DEFAULT

  async execute(model: TModel, context: TContext, flag?: TFlag): Promise<Erreur<TModel, TLevel>> {
    return await this.executeInternal(model, model as any, context, flag)
  }

  private async executeInternal(
    currentModel: TModel,
    globalModel: TGlobalModele,
    context: TContext,
    flag?: TFlag,
    stack?: Array<object>,
  ): Promise<Erreur<TModel, TLevel>> {
    // Controle des références circulaires
    if (stack?.some((item) => item === currentModel) === true) {
      throw Error('Référence circulaire detectée')
    }
    const localStack = stack ? [...stack, currentModel] : [currentModel]

    const erreurTrouvées = {
      $estErreur: false,
      $level: null,
      $messageErreur: null,
      $origine: null,
    } as ErreurResult<TLevel>

    // Traite les erreurs sur les membres
    await this.traite$membres(erreurTrouvées, currentModel, globalModel, context, flag, localStack)

    // Traite les erreurs sur le modéle
    await this.traite$modele(erreurTrouvées, currentModel, globalModel, context, flag)

    // Consolide erreur
    if (localStack.length === 1) {
      return this.consolideErreur(erreurTrouvées as Erreur<TModel, TLevel>)
    }

    return erreurTrouvées as Erreur<TModel, TLevel>
  }

  private consolideErreur(erreursTrouvées: Erreur<TModel, TLevel>) {
    return mergeModelState<TModel, ErreurState<TLevel>, object>(
      erreursTrouvées,
      {},
      {
        summarize: (parent, child) => {
          if (child.$estErreur) {
            parent.$estErreur = true
            parent.$level = Math.min(child.$level!, parent.$level ?? 999_999) as TLevel
          }
          return parent
        },
      },
    )
  }

  private async traite$membres(
    erreursTrouvées: ErreurResult<TLevel>,
    currentModel: TModel,
    globalModel: TGlobalModele,
    context: TContext,
    flag?: TFlag,
    stack: Array<object> = [],
  ) {
    // Fusionne les clés du model et des régles en excluant
    // la clé de $model déja taitée
    const modelKeys = objectMergeKeys(currentModel, this.#regles).filter((key) => key !== '$model')

    // Pour chaques membres
    for (const key of modelKeys) {
      // Valeur du membre
      const value = (currentModel as any)[key]

      // Membre Array
      if (Array.isArray(value)) {
        const objectRegles: any = (this.#regles as any)[key] ?? {}
        const validateurObjet = new Validateur<object, any>(objectRegles)

        // Pour chaque instance du tableau execute une validation
        const resultArray = []
        for (const arrayItem of value) {
          const erreursItem = await validateurObjet.executeInternal(arrayItem, globalModel, context as any, flag, stack)
          resultArray.push(erreursItem)
        }
        // Consolide la validation sur le tableau
        const erreursArray = {
          $estErreur: false,
          $level: null,
          $messageErreur: null,
          $origine: null,
        }

        // Affecte le résultat
        erreursTrouvées[key] = Object.assign(resultArray, erreursArray) as Erreur<object, TLevel>
      }
      // Membre Object
      else if (value instanceof Object) {
        const reglesObjet: any = (this.#regles as any)[key] ?? {}
        const validateurObjet = new Validateur<any, TContext, TLevel, TFlag>(reglesObjet)
        erreursTrouvées[key] = await validateurObjet.executeInternal(value, globalModel, context, flag, stack)
      }
      // Membre autre : bool, number, string, Date
      else {
        const reglesMembre = (this.#regles as any)[key] as ReglesMembre<
          TModel,
          any,
          TContext,
          TLevel,
          TFlag,
          TGlobalModele
        >

        // Vérifie que c'est bien un objet régles de membre
        // et non un objet imbriqué ou un tableau dont le modéle est undefined
        if (Validateur.testEstObjetRegles(reglesMembre) === false) continue

        const erreur = (erreursTrouvées[key] ?? {
          $estErreur: false,
          $level: null,
          $messageErreur: null,
          $origine: null,
        }) as Erreur<any, TLevel>

        if (reglesMembre) {
          // Extrait les clée
          const clesMembre = this.extraitClesObjetRegles(reglesMembre, flag)

          // Execute chaque régle jusqu'a ce qu'une régle tombe en échec
          for (const cleRegle of clesMembre) {
            const regle = reglesMembre[cleRegle]

            //Vérifie la/les pré-condition
            const preconditionOk = this.traite$membreEstPreconditionsOk(
              regle!,
              cleRegle,
              value,
              currentModel,
              globalModel,
              context,
            )
            if (preconditionOk === false) continue

            // Execute le validateur
            try {
              const resultat = await regle!.validateur(value, currentModel, globalModel, context)
              if (this.evaluerResultatEtAffecterErreur(erreur, resultat, regle!, cleRegle)) {
                break
              }
            } catch (ex) {
              console.error(
                `La régle '${cleRegle}' a renvoyée une erreur sur la propriété '${value}' avec le message : '${ex}'`,
              )
              continue
            }
          }
        }
        erreursTrouvées[key] = erreur
      }
    }
  }
  private traite$membreEstPreconditionsOk<TGlobalModele extends object>(
    regle: Regle<TModel, any, TContext, TLevel, TFlag, TGlobalModele>,
    cleRegle: string,
    value: any,
    parent: TModel,
    globalModel: TGlobalModele,
    context: TContext,
  ): boolean {
    // Tester la/les pré-condition
    try {
      if (Array.isArray(regle.pre)) {
        let preconditionKO = false
        for (const pre of regle.pre) {
          const resultatPré = pre(value, parent, globalModel, context)
          preconditionKO = resultatPré === false || (typeof resultatPré === 'string' && resultatPré.length > 0)
          if (preconditionKO) break
        }
        if (preconditionKO) return false
      } else if (typeof regle.pre === 'function') {
        const resultatPré = regle.pre(value, parent, globalModel, context)
        const preconditionKO = resultatPré === false || (typeof resultatPré === 'string' && resultatPré.length > 0)
        if (preconditionKO) return false
      }
    } catch (ex) {
      // L'echec de la précondition bloque l'execution de la regle
      console.error(
        `Une précondition de la regle '${cleRegle}' a renvoyée une erreur sur la propriété '${value}' avec le message : '${ex}'`,
      )
      return false
    }

    return true
  }

  private async traite$modele(
    erreurTrouvées: ErreurResult<TLevel>,
    model: TModel,
    globalModel: TGlobalModele,
    context: TContext,
    flag?: TFlag,
  ) {
    if (typeof this.#regles.$model !== 'object') return

    // Clée sur le model entier
    const reglesModel = this.#regles.$model
    if (reglesModel) {
      const clesMembre = this.extraitClesObjetRegles(reglesModel, flag)

      for (const cleRegle of clesMembre) {
        const regle = reglesModel[cleRegle]

        // Verifier la pré-condition
        if (this.traite$modeleEstPreconditionsOk(regle!, cleRegle, model, globalModel, context) === false) continue

        // Execute le validateur
        try {
          const resultats = (await regle?.validateur(model, globalModel, context)) as ModelValidateurResult<any>
          if (resultats === null || resultats === undefined) continue
          for (const cleeMembre in resultats) {
            const resultatMembre = resultats[cleeMembre] as ValidateurResult
            if (cleeMembre === '') {
              // Ne stocke pas l'erreur s'il existe deja une erreur de niveau inférieur
              if (this.estLevelPrioritaire(erreurTrouvées.$level, regle?.level)) continue
              this.evaluerResultatEtAffecterErreur(erreurTrouvées, resultatMembre, regle!, cleRegle)
            } else {
              // Todo en DEEP
              const cheminBoucle = []
              let erreursBoucle = erreurTrouvées
              for (const token of cleeMembre.split('.')) {
                cheminBoucle.push(token)
                const cleeBoucle = cheminBoucle.join('.')

                // Selon que l'object erreur est un objet ou un array
                const erreurIndex = /^\d+$/.test(token) ? parseInt(token) : token

                if (typeof erreurIndex === 'number') {
                  if (Array.isArray(erreursBoucle) === false) {
                    console.error(
                      `Le validateur d'objet ${cleRegle} à renvoyé une erreur sur le membre ${cleeBoucle} qui n'est pas un tableau`,
                    )
                    break
                  } else if (erreurIndex < 0 || erreurIndex > erreursBoucle.length - 1) {
                    console.error(
                      `Le validateur d'objet ${cleRegle} à renvoyé une erreur sur le membre ${cleeBoucle} du tableau dont l'indice est invalide`,
                    )
                    break
                  }
                }

                const erreur: ErreurState<TLevel> = erreursBoucle[erreurIndex] ?? {
                  $estErreur: false,
                  $level: null,
                  $messageErreur: null,
                  $origine: null,
                }

                if (cleeBoucle === cleeMembre) {
                  // Ne stocke pas l'erreur s'il existe deja une erreur de niveau inférieur
                  if (this.estLevelPrioritaire(erreur.$level, regle?.level)) continue
                  this.evaluerResultatEtAffecterErreur(erreur, resultatMembre, regle!, cleRegle)
                }
                erreursBoucle[erreurIndex] = erreur
                erreursBoucle = erreur as ErreurResult<TLevel>
              }
            }
          }
        } catch (ex) {
          console.error(`La régle '${cleRegle}' a renvoyée une erreur avec le message : '${ex}'`)
          continue
        }
      }
    }
  }
  private traite$modeleEstPreconditionsOk(
    regle: RegleModel<TModel, TContext, TLevel, TFlag, TGlobalModele>,
    cleRegle: string,
    model: TModel,
    globalModel: TGlobalModele,
    context: TContext,
  ): boolean {
    // Tester la/les pré-condition
    try {
      if (Array.isArray(regle.pre)) {
        const preArray = regle.pre as RegleModelePreCallback<TModel, TContext, TGlobalModele>[]
        let preconditionKO = false
        for (const pre of preArray) {
          const resultatPré = pre(model, globalModel, context)
          preconditionKO = resultatPré === false || (typeof resultatPré === 'string' && resultatPré.length > 0)
          if (preconditionKO) break
        }
        if (preconditionKO) return false
      } else if (typeof regle.pre === 'function') {
        const resultatPré = regle.pre(model, globalModel, context)
        const preconditionKO = resultatPré === false || (typeof resultatPré === 'string' && resultatPré.length > 0)
        if (preconditionKO) return false
      }
    } catch (ex) {
      // L'echec de la précondition bloque l'execution de la regle
      console.error(`Une précondition de la regle '${cleRegle}' a renvoyée une erreur avec le message : '${ex}'`)
      return false
    }
    return true
  }

  /**
   * Vérifie que l'objet est un objet Regle
   * @param regles
   * @returns Retourne false si ce n'est pas un objet Regles.
   */
  private static testEstObjetRegles(regles: { [key: string]: { validateur: any } }): boolean {
    if (!regles) return true
    return Object.keys(regles).some((key) => typeof (regles ?? {})[key]?.validateur === 'function')
  }

  /**
   *
   * @param regles
   * @param level
   * @returns Les clés d'un objet régles filtrées et triés selon le TLevel
   */
  private extraitClesObjetRegles(regles: { [key: string]: { level?: TLevel; flag?: TFlag } }, flag?: TFlag): string[] {
    return Object.keys(regles)
      .filter((clee) => {
        if (flag === undefined || flag === 0) return true
        const regle = regles[clee]
        if (!regle || regle.flag === undefined || regle.flag === 0) return true
        return (regle.flag & flag) !== 0
      })
      .sort((k1, k2) => {
        const level1 = regles[k1]?.level ?? LEVEL_DEFAULT_VALUE
        const level2 = regles[k2]?.level ?? LEVEL_DEFAULT_VALUE
        return level1 - level2
      })
  }

  /**
   * Applique un resultat de test OK ou KO à un objet erreur
   * @param erreur Erreur à muter
   * @param resultat Resultat positif ou négatif à un test
   * @param regle Régle utilisée pour récupération de son message et de son Level
   * @param cleRegle Nom de la régle
   * @returns true si le résultat a été appliqué et false si la régle n'a pas été appliquée
   */
  private evaluerResultatEtAffecterErreur(
    erreur: Erreur<any, TLevel> | ErreurState<TLevel>,
    resultat: ValidateurResult,
    regle: { message?: string; level?: TLevel },
    cleRegle: string,
  ): boolean {
    const newErr = this.construireErreur(resultat, regle, cleRegle)
    if (newErr) {
      Object.assign(erreur, newErr)
      return true
    }
    return false
  }

  private construireErreur(
    resultat: ValidateurResult,
    regle: { message?: string; level?: TLevel },
    cleRegle: string,
  ): Erreur<object, TLevel> | null {
    if (resultat === false) {
      return {
        $estErreur: true,
        $messageErreur: regle.message ?? MESSAGE_DEFAULT,
        $level: regle.level ?? (LEVEL_DEFAULT_VALUE as TLevel),
        $origine: cleRegle,
      }
    } else if (typeof resultat === 'string' && resultat !== '') {
      return {
        $estErreur: true,
        $messageErreur: resultat ?? regle.message ?? MESSAGE_DEFAULT,
        $level: regle.level ?? (LEVEL_DEFAULT_VALUE as TLevel),
        $origine: cleRegle,
      }
    }
    return null
  }

  /**
   * Compare deux niveaux d'erreur
   * @param levelCourant L'erreur en cours
   * @param levelFuture L'erreur à vérifier
   * @returns true si le niveau courant est prioritaire
   */
  private estLevelPrioritaire(levelCourant?: TLevel | null, levelFuture?: TLevel | null): boolean {
    if (levelCourant === null || levelCourant === undefined) return false
    return levelCourant <= (levelFuture ?? LEVEL_DEFAULT_VALUE)
  }
}

